import React, { createContext, useContext, useEffect, useState } from 'react';
import { Logo } from '../ds/components/Brujula';
import '../ds/tokens/fonts.css';
import '../ds/tokens/tokens.css';

// Agencia de onboarding: el front de la persona (prototipo con datos ficticios).
// Spec: specs/agencia-onboarding/spec.md · Diseño: cerebro/guia-propio/onboarding-agentico-partners.md
// El contenido llega de netlify/functions/agencia-onboarding.js y solo con el código (An, 04/11/2026).
// Al entrar, primero la explicación del producto con su video (marca GUIA); después el front, que lleva
// la marca del partner (marca blanca): todo el color sale de c.marca.

const Contenido = createContext(null);
const useC = () => useContext(Contenido);
const GUARDADO = 'agencia-onboarding-codigo';
const EN_DEMO = 'agencia-onboarding-demo';

const MODOS = [
  { id: 'arrancar', t: 'Arrancar', agente: 'Primeros pasos', icono: 'brujula' },
  { id: 'aprender', t: 'Aprender', agente: 'Capacitación', icono: 'libro' },
  { id: 'practicar', t: 'Practicar', agente: 'Misiones', icono: 'bandera' },
  { id: 'preguntar', t: 'Preguntar', agente: 'Soporte', icono: 'globo' },
  { id: 'equipo', t: 'Equipo', agente: 'Misiones', icono: 'manos' },
];

const TRAZOS = {
  brujula: <><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></>,
  libro: <><path d="M4 5.5C4 4.7 4.7 4 5.5 4H11v15H5.5c-.8 0-1.5-.7-1.5-1.5z" /><path d="M20 5.5c0-.8-.7-1.5-1.5-1.5H13v15h5.5c.8 0 1.5-.7 1.5-1.5z" /></>,
  bandera: <><path d="M6 21V4" /><path d="M6 4h11l-2 4 2 4H6" /></>,
  globo: <><path d="M5 5h14a1 1 0 011 1v9a1 1 0 01-1 1h-8l-4 3v-3H5a1 1 0 01-1-1V6a1 1 0 011-1z" /></>,
  manos: <><circle cx="8" cy="8" r="3" /><circle cx="16" cy="8" r="3" /><path d="M3 20c0-3 2.2-5 5-5s5 2 5 5" /><path d="M11 20c0-3 2.2-5 5-5s5 2 5 5" /></>,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  persona: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></>,
  flecha: <path d="M5 12h14M13 6l6 6-6 6" />,
  reloj: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  cerrar: <path d="M6 6l12 12M18 6L6 18" />,
};

function Icono({ nombre, className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {TRAZOS[nombre]}
    </svg>
  );
}

function Tarjeta({ children, className = '' }) {
  return <section className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`}>{children}</section>;
}

function Titulo({ children }) {
  return <h3 className="text-base font-semibold text-slate-900">{children}</h3>;
}

function Barra({ valor, total }) {
  const { marca } = useC();
  return (
    <div className="h-2 w-full rounded-full" style={{ background: marca.suave }}>
      <div className="h-2 rounded-full transition-all duration-500"
        style={{ width: `${Math.round((valor / total) * 100)}%`, background: marca.principal }} />
    </div>
  );
}

const leer = (clave = GUARDADO) => { try { return sessionStorage.getItem(clave) || ''; } catch { return ''; } };
const guardar = (v, clave = GUARDADO) => { try { sessionStorage.setItem(clave, v); } catch { /* sin almacenamiento: se pide de nuevo */ } };

async function pedir(codigo, extra) {
  const r = await fetch('/.netlify/functions/agencia-onboarding', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ codigo, ...extra }),
  });
  if (r.status === 401) throw new Error('Ese código no es.');
  if (!r.ok) throw new Error('No pude abrirlo. Probá de nuevo en un rato.');
  return r;
}

const pedirContenido = codigo => pedir(codigo).then(r => r.json());

// ── La puerta: sin código no hay contenido ─────────────────────────────

function Puerta({ onEntrar }) {
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);

  const entrar = async e => {
    e.preventDefault();
    if (!codigo.trim()) return;
    setCargando(true);
    setError('');
    try {
      const c = await pedirContenido(codigo.trim());
      guardar(codigo.trim());
      onEntrar(c);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4"
      style={{ fontFamily: 'var(--guia-font-body)' }}>
      <form onSubmit={entrar} className="w-full max-w-sm">
        <Logo sobreClaro variante="horizontal" style={{ height: 40, width: 'auto' }} aria-label="GUIA" />
        <h1 className="mt-8 text-3xl leading-tight text-black" style={{ fontFamily: 'var(--guia-font-display)' }}>
          Agencia de software onboarding
        </h1>
        <p className="mt-1 text-lg" style={{ fontFamily: 'var(--guia-font-sub)', fontWeight: 600, fontStyle: 'italic', color: 'var(--guia-orquidea)' }}>
          Un desarrollo de GUIA
        </p>
        <label htmlFor="codigo" className="mt-8 block text-base text-black">Código de acceso</label>
        <input id="codigo" value={codigo} onChange={e => setCodigo(e.target.value)} autoComplete="off" autoFocus
          className="mt-2 w-full rounded-lg border-2 border-black/10 px-3 py-2.5 text-lg focus:border-[color:var(--guia-orquidea)] focus:outline-none" />
        {error && <p className="mt-2 text-base text-red-700">{error}</p>}
        <button type="submit" disabled={cargando}
          className="mt-4 w-full rounded-lg px-4 py-2.5 text-lg text-white transition-opacity disabled:opacity-50"
          style={{ background: 'var(--guia-orquidea)' }}>
          {cargando ? 'Abriendo…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}

// ── La explicación: qué es el producto, con video (marca GUIA) ──────────

function Explicacion({ onSeguir }) {
  const { explicacion: e } = useC();
  const [video, setVideo] = useState('');
  const [sinVideo, setSinVideo] = useState(false);

  useEffect(() => {
    let url = '';
    pedir(leer(), { video: true })
      .then(r => r.blob())
      .then(b => { url = URL.createObjectURL(b); setVideo(url); })
      .catch(() => setSinVideo(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: 'var(--guia-font-body)' }}>
      <header className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4">
        <Logo sobreClaro variante="horizontal" style={{ height: 32, width: 'auto' }} aria-label="GUIA" />
        <button type="button" onClick={onSeguir}
          className="text-base underline-offset-4 hover:underline" style={{ color: 'var(--guia-orquidea)' }}>
          {e.boton}
        </button>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-4 md:pt-10" style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
        <h1 className="text-4xl leading-tight md:text-5xl" style={{ fontFamily: 'var(--guia-font-display)' }}>
          Agencia de software onboarding
        </h1>
        <p className="mt-2 text-xl leading-snug" style={{ fontFamily: 'var(--guia-font-sub)', fontWeight: 600, fontStyle: 'italic', color: 'var(--guia-orquidea)' }}>
          {e.bajada}
        </p>
        {e.intro.map(t => <p key={t} className="mt-5 max-w-3xl text-lg leading-snug text-black/80">{t}</p>)}

        <div className="mt-8">
          {video ? (
            <video src={video} controls autoPlay muted playsInline
              className="aspect-video w-full rounded-2xl border border-black/10 bg-white" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-2xl border border-black/10 bg-white px-6 text-center text-base text-black/50">
              {sinVideo ? 'El video no cargó. El resto de la página lo cuenta igual.' : 'Cargando el video…'}
            </div>
          )}
        </div>

        <ul className="mt-10 grid gap-5 md:grid-cols-3 md:gap-8">
          {e.puntos.map(p => (
            <li key={p.t}>
              <p className="text-xl" style={{ fontFamily: 'var(--guia-font-display)' }}>{p.t}</p>
              <p className="mt-0.5 text-lg leading-snug text-black/70">{p.d}</p>
            </li>
          ))}
        </ul>

        <p className="mt-10 text-lg leading-snug">{e.adentro}</p>
        <button type="button" onClick={onSeguir}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-lg text-white transition-opacity hover:opacity-90 sm:w-auto"
          style={{ background: 'var(--guia-orquidea)' }}>
          {e.boton}
          <Icono nombre="flecha" className="h-5 w-5" />
        </button>
      </main>
    </div>
  );
}

// ── Camino: la progresión contra una misma ─────────────────────────────

function Camino() {
  const { marca, persona, etapas, vos } = useC();
  return (
    <aside className="space-y-4">
      <Tarjeta>
        <p className="text-sm font-medium text-slate-500">
          Semana {persona.semana} de {persona.de}
        </p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900">Tu camino</h2>
        <ol className="mt-4 space-y-0">
          {etapas.map((e, i) => (
            <li key={e.t} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2"
                  style={{
                    borderColor: e.hecho || e.actual ? marca.principal : '#CBD5E1',
                    background: e.hecho ? marca.principal : '#fff',
                    color: '#fff',
                  }}>
                  {e.hecho && <Icono nombre="check" className="h-3.5 w-3.5" />}
                  {e.actual && <span className="h-2 w-2 rounded-full" style={{ background: marca.principal }} />}
                </span>
                {i < etapas.length - 1 && (
                  <span className="w-0.5 flex-1 min-h-[18px]" style={{ background: e.hecho ? marca.principal : '#E2E8F0' }} />
                )}
              </div>
              <p className={`pb-3 text-sm ${e.actual ? 'font-semibold text-slate-900' : e.hecho ? 'text-slate-600' : 'text-slate-400'}`}>
                {e.t}
              </p>
            </li>
          ))}
        </ol>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Vos, contra vos</Titulo>
        <div className="mt-3 space-y-3 text-sm">
          <div>
            <p className="text-slate-600">{vos.tarea}</p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-slate-400 line-through">{vos.antes}</span>
              <Icono nombre="flecha" className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-xl font-semibold" style={{ color: marca.principal }}>{vos.ahora}</span>
            </p>
            <p className="text-xs text-slate-500">la primera vez · esta semana</p>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-slate-600">{vos.disparador}</p>
            <div className="mt-2 flex gap-1.5">
              {Array.from({ length: vos.de }, (_, n) => (
                <span key={n} className="h-2 flex-1 rounded-full"
                  style={{ background: n < vos.semanas ? marca.acento : '#E2E8F0' }} />
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-500">{vos.semanas} semanas seguidas</p>
          </div>
        </div>
      </Tarjeta>
    </aside>
  );
}

// ── Arrancar · agente de Primeros pasos ────────────────────────────────

function Arrancar() {
  const { marca, arrancar: a } = useC();
  const [pasos, setPasos] = useState(a.victoria.pasos.map(() => true));
  const [disparador, setDisparador] = useState(a.disparadores[0]);
  const listos = pasos.filter(Boolean).length;

  return (
    <div className="space-y-4">
      <Tarjeta>
        <Titulo>{a.titulo}</Titulo>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm font-medium" style={{ color: marca.principal }}>
              Ya te lo resuelve {marca.herramienta}
            </p>
            <ul className="mt-2 space-y-2">
              {a.resuelve.map(t => (
                <li key={t} className="flex gap-2 rounded-xl px-3 py-2 text-sm text-slate-800" style={{ background: marca.suave }}>
                  <Icono nombre="check" className="mt-0.5 h-4 w-4 shrink-0" />{t}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Lo que sigue</p>
            <ul className="mt-2 space-y-2">
              {a.mano.map((t, i) => (
                <li key={t} className="flex gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-700">
                  <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-slate-300" />
                  <span>{t}{i === 0 && <span className="ml-1 font-medium" style={{ color: marca.acento }}>· {a.marcaMano}</span>}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta>
        <div className="flex items-center justify-between gap-3">
          <Titulo>{a.victoria.titulo}</Titulo>
          <span className="flex items-center gap-1 text-xs text-slate-500"><Icono nombre="reloj" className="h-4 w-4" />{a.victoria.min} min</span>
        </div>
        <ol className="mt-3 space-y-2">
          {a.victoria.pasos.map((p, i) => (
            <li key={p}>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 hover:bg-slate-50">
                <input type="checkbox" className="mt-1 h-4 w-4" style={{ accentColor: marca.principal }}
                  checked={pasos[i]} onChange={() => setPasos(pasos.map((v, j) => (j === i ? !v : v)))} />
                <span className={`text-sm ${pasos[i] ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{p}</span>
              </label>
            </li>
          ))}
        </ol>
        {listos === pasos.length && (
          <p className="mt-3 rounded-xl px-3 py-2 text-sm font-medium" style={{ background: marca.suave, color: marca.principal }}>
            {a.victoria.hecha}
          </p>
        )}
      </Tarjeta>

      <Tarjeta>
        <Titulo>Cuándo la usás</Titulo>
        <div className="mt-3 space-y-2">
          {a.disparadores.map(d => (
            <label key={d} className="flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm"
              style={{ borderColor: disparador === d ? marca.principal : '#E2E8F0', background: disparador === d ? marca.suave : '#fff' }}>
              <input type="radio" name="disparador" className="h-4 w-4" style={{ accentColor: marca.principal }}
                checked={disparador === d} onChange={() => setDisparador(d)} />
              {d}
            </label>
          ))}
        </div>
      </Tarjeta>
    </div>
  );
}

// ── Aprender · agente de Capacitación ──────────────────────────────────

function Aprender() {
  const { marca, aprender: a } = useC();
  const [lecciones, setLecciones] = useState(a.lecciones);
  return (
    <div className="space-y-4">
      <section className="rounded-2xl p-5 text-white" style={{ background: marca.principal }}>
        <p className="text-sm font-medium opacity-80">Próximo encuentro</p>
        <p className="mt-1 text-xl font-semibold">{a.encuentro.titulo}</p>
        <p className="mt-2 text-sm opacity-90">{a.encuentro.cuando}</p>
        <p className="mt-3 text-sm opacity-90">{a.encuentro.porque}</p>
      </section>

      <Tarjeta>
        <Titulo>Antes del encuentro</Titulo>
        <ul className="mt-3 space-y-2">
          {lecciones.map((l, i) => (
            <li key={l.t}>
              <button type="button"
                onClick={() => setLecciones(lecciones.map((x, j) => (j === i ? { ...x, hecha: !x.hecha } : x)))}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-left hover:bg-slate-50">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ background: l.hecha ? marca.principal : marca.suave, color: l.hecha ? '#fff' : marca.principal }}>
                  {l.hecha ? <Icono nombre="check" className="h-4 w-4" /> : <span className="text-xs font-semibold">{i + 1}</span>}
                </span>
                <span className="flex-1 text-sm text-slate-800">{l.t}</span>
                <span className="text-xs text-slate-500">{l.min} min</span>
              </button>
            </li>
          ))}
        </ul>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Del Encuentro 1</Titulo>
        <p className="mt-2 text-sm text-slate-600">{a.material}</p>
        <button type="button" className="mt-3 rounded-xl border px-4 py-2 text-sm font-medium"
          style={{ borderColor: marca.principal, color: marca.principal }}>
          Abrir la guía
        </button>
      </Tarjeta>
    </div>
  );
}

// ── Practicar · agente de Misiones ─────────────────────────────────────

function Practicar() {
  const { marca, practicar: m } = useC();
  const [partes, setPartes] = useState(m.partes.map((_, i) => i === 0));
  const [relato, setRelato] = useState('');
  const [pista, setPista] = useState(false);
  const [respuestas, setRespuestas] = useState({});
  const hechas = partes.filter(Boolean).length;
  const contado = relato.trim().length >= 20;

  return (
    <div className="space-y-4">
      <Tarjeta>
        <p className="text-sm font-medium" style={{ color: marca.acento }}>{m.numero}</p>
        <h3 className="mt-1 text-xl font-semibold text-slate-900">{m.titulo}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">{m.relato}</p>
        <div className="mt-4">
          <div className="mb-2 flex justify-between text-xs text-slate-500">
            <span>{hechas} de {m.partes.length}</span><span>{m.vence}</span>
          </div>
          <Barra valor={hechas} total={m.partes.length} />
        </div>
        <ul className="mt-4 space-y-2">
          {m.partes.map((p, i) => (
            <li key={p}>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 hover:bg-slate-50">
                <input type="checkbox" className="h-4 w-4" style={{ accentColor: marca.principal }}
                  checked={partes[i]} onChange={() => setPartes(partes.map((v, j) => (j === i ? !v : v)))} />
                <span className={`text-sm ${partes[i] ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{p}</span>
              </label>
            </li>
          ))}
        </ul>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Contame cómo lo hiciste</Titulo>
        <textarea value={relato} onChange={e => setRelato(e.target.value)} rows={3} placeholder={m.placeholder}
          className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': marca.principal }} />
        <button type="button" disabled={!contado} onClick={() => setPista(true)}
          className="mt-3 rounded-xl px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-40"
          style={{ background: marca.principal }}>
          Ver la pista
        </button>
        {pista && (
          <p className="mt-3 rounded-xl px-3 py-2 text-sm text-slate-900" style={{ background: marca.suave }}>{m.pista}</p>
        )}
      </Tarjeta>

      <Tarjeta>
        <Titulo>Chequeo corto</Titulo>
        <div className="mt-3 space-y-5">
          {m.chequeo.map((q, qi) => {
            const elegida = respuestas[qi];
            return (
              <div key={q.p}>
                <p className="text-sm font-medium text-slate-800">{q.p}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.opciones.map((o, oi) => {
                    const marcada = elegida === oi;
                    const color = !marcada ? undefined : oi === q.bien ? marca.principal : '#B45309';
                    return (
                      <button key={o} type="button" onClick={() => setRespuestas({ ...respuestas, [qi]: oi })}
                        className="rounded-full border px-3 py-1.5 text-sm"
                        style={{ borderColor: color || '#E2E8F0', color: color || '#334155', background: marcada ? '#F8FAFC' : '#fff' }}>
                        {o}
                      </button>
                    );
                  })}
                </div>
                {elegida !== undefined && (
                  <p className="mt-2 text-sm text-slate-600">{elegida === q.bien ? 'Eso. ' : 'Casi. '}{q.porque}</p>
                )}
              </div>
            );
          })}
        </div>
      </Tarjeta>
    </div>
  );
}

// ── Preguntar · agente de Soporte (también abre desde cualquier modo) ──

function Preguntar({ charla, setCharla, conPersona, setConPersona }) {
  const { marca, preguntar: p } = useC();
  const [texto, setTexto] = useState('');

  const enviar = e => {
    e.preventDefault();
    if (!texto.trim()) return;
    setCharla([...charla, { de: 'persona', t: texto.trim() }, { de: 'agente', t: p.sinRespuesta, pasar: true }]);
    setTexto('');
  };

  return (
    <div className="flex flex-col">
      <p className="text-xs text-slate-500">{p.aviso}</p>
      <div className="mt-4 space-y-3">
        {charla.map((m, i) => (
          <div key={i} className={`flex ${m.de === 'persona' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.de === 'persona' ? 'text-white' : 'text-slate-800'}`}
              style={{ background: m.de === 'persona' ? marca.principal : marca.suave }}>
              <p>{m.t}</p>
              {m.fuente && <p className="mt-2 border-t border-slate-300/60 pt-2 text-xs text-slate-600">{m.fuente}</p>}
              {m.pasar && !conPersona && (
                <button type="button" onClick={() => setConPersona(true)}
                  className="mt-2 rounded-lg bg-white px-3 py-1.5 text-xs font-medium" style={{ color: marca.principal }}>
                  Sí, pasame
                </button>
              )}
            </div>
          </div>
        ))}
        {conPersona && <p className="text-center text-xs text-slate-500">{p.pasado}</p>}
      </div>
      <form onSubmit={enviar} className="mt-5 flex gap-2">
        <input value={texto} onChange={e => setTexto(e.target.value)} placeholder="Escribí tu pregunta"
          className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': marca.principal }} />
        <button type="submit" className="rounded-xl px-4 py-2 text-sm font-medium text-white" style={{ background: marca.principal }}>
          Preguntar
        </button>
      </form>
      <button type="button" onClick={() => setConPersona(true)}
        className="mt-3 flex items-center gap-2 self-start text-sm font-medium" style={{ color: marca.principal }}>
        <Icono nombre="persona" className="h-4 w-4" />Hablar con una persona
      </button>
    </div>
  );
}

// ── Equipo · cooperación ───────────────────────────────────────────────

function Equipo() {
  const { marca, equipo: q } = useC();
  const [pedidos, setPedidos] = useState(q.meta.van);
  const [aportado, setAportado] = useState(false);
  const [posta, setPosta] = useState('');
  const [postas, setPostas] = useState(q.postas);
  const [pedida, setPedida] = useState(null);

  return (
    <div className="space-y-4">
      <Tarjeta>
        <p className="text-sm font-medium" style={{ color: marca.acento }}>{q.meta.rotulo}</p>
        <h3 className="mt-1 text-lg font-semibold text-slate-900">{q.meta.titulo}</h3>
        <div className="mt-4">
          <div className="mb-2 flex justify-between text-sm">
            <span className="font-semibold" style={{ color: marca.principal }}>{pedidos} de {q.meta.total}</span>
            <span className="text-slate-500">faltan {q.meta.total - pedidos}</span>
          </div>
          <Barra valor={pedidos} total={q.meta.total} />
        </div>
        <button type="button" disabled={aportado} onClick={() => { setPedidos(pedidos + 1); setAportado(true); }}
          className="mt-4 rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{ background: marca.principal }}>
          {aportado ? 'Sumaste tu pedido' : 'Sumé un pedido'}
        </button>
      </Tarjeta>

      <Tarjeta>
        <Titulo>{q.postaTitulo}</Titulo>
        <ul className="mt-3 space-y-2">
          {postas.map(p => (
            <li key={p.t} className="rounded-xl px-3 py-2.5 text-sm" style={{ background: marca.suave }}>
              <p className="text-slate-800">{p.t}</p>
              <p className="mt-1 text-xs text-slate-500">{p.de}</p>
            </li>
          ))}
        </ul>
        <form className="mt-3 flex gap-2" onSubmit={e => {
          e.preventDefault();
          if (!posta.trim()) return;
          setPostas([...postas, { t: posta.trim(), de: q.postaPropia }]);
          setPosta('');
        }}>
          <input value={posta} onChange={e => setPosta(e.target.value)} placeholder="Lo que te destrabó a vos"
            className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': marca.principal }} />
          <button type="submit" className="rounded-xl border px-4 py-2 text-sm font-medium"
            style={{ borderColor: marca.principal, color: marca.principal }}>
            Dejar la posta
          </button>
        </form>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Pedile una mano a alguien</Titulo>
        <ul className="mt-3 space-y-2">
          {q.companeros.map(c => (
            <li key={c.n} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                style={{ background: marca.suave, color: marca.principal }}>{c.n[0]}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">{c.n} · {c.p}</p>
                <p className="text-xs text-slate-500">{c.hizo}</p>
              </div>
              <button type="button" onClick={() => setPedida(c.n)}
                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium"
                style={{ background: pedida === c.n ? marca.principal : marca.suave, color: pedida === c.n ? '#fff' : marca.principal }}>
                {pedida === c.n ? 'Le avisé' : 'Pedir una mano'}
              </button>
            </li>
          ))}
        </ul>
      </Tarjeta>
    </div>
  );
}

const PANELES = { arrancar: Arrancar, aprender: Aprender, practicar: Practicar, equipo: Equipo };

function Front() {
  const { marca, persona, preguntar } = useC();
  const [modo, setModo] = useState('practicar');
  const [chatAbierto, setChatAbierto] = useState(false);
  const [charla, setCharla] = useState(preguntar.charla);
  const [conPersona, setConPersona] = useState(false);
  const Panel = PANELES[modo];
  const chat = { charla, setCharla, conPersona, setConPersona };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white"
              style={{ background: marca.principal }}>{marca.inicial}</span>
            <span className="text-base font-semibold" style={{ color: marca.principal }}>{marca.herramienta}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium">{persona.nombre} · {persona.puesto}</p>
              <p className="text-xs text-slate-500">{persona.empresa}</p>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold"
              style={{ background: marca.suave, color: marca.principal }}>{persona.nombre[0]}</span>
          </div>
        </div>
      </header>

      <nav className="sticky top-0 z-10 border-b border-slate-200 bg-white" aria-label="Modos">
        <div className="mx-auto flex max-w-6xl overflow-x-auto px-2" role="tablist" style={{ scrollbarWidth: 'none' }}>
          {MODOS.map(m => {
            const activo = m.id === modo;
            return (
              <button key={m.id} type="button" role="tab" aria-selected={activo}
                onClick={() => { setModo(m.id); setChatAbierto(false); }}
                className="group relative flex shrink-0 items-center gap-2.5 px-4 py-3 text-left transition-colors"
                style={{ color: activo ? marca.principal : '#64748B' }}>
                <Icono nombre={m.icono} className="h-5 w-5" />
                <span className="leading-tight">
                  <span className={`block text-sm ${activo ? 'font-semibold' : 'font-medium group-hover:text-slate-800'}`}>{m.t}</span>
                  <span className="block text-[11px] text-slate-400">{m.agente}</span>
                </span>
                <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full transition-opacity"
                  style={{ background: marca.principal, opacity: activo ? 1 : 0 }} />
              </button>
            );
          })}
        </div>
      </nav>

      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-6 pb-24 lg:grid-cols-[280px_1fr]">
        <div className="order-2 lg:order-1"><Camino /></div>

        <div className="order-1 min-w-0 lg:order-2">
          <div key={modo} style={{ animation: 'fadeSlideUp 0.35s ease-out' }}>
            {modo === 'preguntar' ? <Tarjeta><Preguntar {...chat} /></Tarjeta> : <Panel />}
          </div>
        </div>
      </main>

      {modo !== 'preguntar' && (
        <div className="fixed bottom-4 right-4 z-20 flex flex-col items-end gap-3">
          {chatAbierto && (
            <div className="max-h-[70vh] w-[min(380px,calc(100vw-2rem))] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
              style={{ animation: 'fadeSlideUp 0.25s ease-out' }}>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-900">Preguntar</p>
                <button type="button" onClick={() => setChatAbierto(false)} aria-label="Cerrar el chat"
                  className="rounded-lg p-1 text-slate-500 hover:bg-slate-100">
                  <Icono nombre="cerrar" className="h-4 w-4" />
                </button>
              </div>
              <Preguntar {...chat} />
            </div>
          )}
          <button type="button" onClick={() => setChatAbierto(!chatAbierto)} aria-label="Abrir el chat"
            className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg"
            style={{ background: marca.principal }}>
            <Icono nombre={chatAbierto ? 'cerrar' : 'globo'} className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function AgenciaOnboarding() {
  const [contenido, setContenido] = useState(null);
  const [probando, setProbando] = useState(() => Boolean(leer()));
  const [enDemo, setEnDemo] = useState(() => Boolean(leer(EN_DEMO)));

  useEffect(() => {
    const previo = leer();
    if (!previo) return;
    pedirContenido(previo).then(setContenido).catch(() => {}).finally(() => setProbando(false));
  }, []);

  const verDemo = () => { guardar('1', EN_DEMO); setEnDemo(true); window.scrollTo(0, 0); };

  if (contenido) {
    return (
      <Contenido.Provider value={contenido}>
        {enDemo ? <Front /> : <Explicacion onSeguir={verDemo} />}
      </Contenido.Provider>
    );
  }
  if (probando) return <div className="min-h-screen bg-slate-50" />;
  return <Puerta onEntrar={setContenido} />;
}
